/* ============================================================
   glow-pdp.js — התנהגות קלה ל-PDP של Glow.
   glow-inview: מוסיף .is-inview כשהאלמנט נכנס ל-viewport (קו תחתון מונפש
   ב-glow-testimonial). מכבד prefers-reduced-motion. אין תלות חיצונית.

   idempotency: הקובץ נכלל בשני סקשנים (main-product + glow-related-products)
   שיכולים להופיע יחד באותו עמוד (PDP). כדי שטעינה/הרצה כפולה לא תזרוק
   "Identifier 'GlowInview' has already been declared" ולא תכפיל מאזיני-שיתוף,
   כל המודול עטוף ב-IIFE (מרחיק את ה-class מההיקף הגלובלי) עם guard חד-פעמי.
   ============================================================ */
(function () {
  if (window.__glowPdpInit) return;
  window.__glowPdpInit = true;

  class GlowInview extends HTMLElement {
    connectedCallback() {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce || !('IntersectionObserver' in window)) {
        this.classList.add('is-inview');
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-inview');
              this.observer.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.35 }
      );

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  if (!customElements.get('glow-inview')) {
    customElements.define('glow-inview', GlowInview);
  }

  /* Messenger share: המובייל פותח את האפליקציה דרך fb-messenger:// (ה-href).
     בדסקטופ הסכימה לא נפתרת — מיירטים ופותחים messenger.com בטאב חדש (ללא app_id).
     זיהוי מובייל שמרני (UA); בכל ספק — נשארים עם ה-deeplink. */
  function glowInitMessengerShare() {
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    for (const link of document.querySelectorAll('[data-glow-messenger]')) {
      if (isMobile) continue;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const fallback = link.dataset.glowMessengerFallback || 'https://www.messenger.com/new';
        window.open(fallback, '_blank', 'noopener');
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', glowInitMessengerShare, { once: true });
  } else {
    glowInitMessengerShare();
  }

  /* אנליטיקה בלבד (delegation גלובלי, passive — בלי preventDefault, הניווט נשמר):
     - glow_share       על קליק בקישור שיתוף PDP (WhatsApp/Messenger).
     - glow_select_item על קליק בכרטיס related ב-PDP (list:'pdp_related'),
       כולל שכפולי ה-loop של glow-loop-carousel (delegation תופס את כולם).
     המודול עטוף ב-IIFE עם guard חד-פעמי (__glowPdpInit) → מאזין יחיד. לא-קריטי. */
  function glowPublish(name, payload) {
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') return;
    try {
      window.Shopify.analytics.publish(name, payload);
    } catch (e) { /* אנליטיקה לא-קריטית */ }
  }

  /* 30.9.2026 סבב 3 · הכפתור הצף מזכוכית (snippets/sticky-atc · glow-pdp.css §3.11).
     במובייל הוא קפסולה צפה בתחתית, בדיוק בגובה של ה-launcher של Intercom. כשהקפסולה
     מופיעה (Symmetry מסיר sticky-atc--out) מודדים את ה-launcher פעם אחת, ואם הוא
     בפס התחתון, מפנים לו את הצד שלו (--glow-sticky-left / --glow-sticky-right).
     בלי מאזין גלילה: MutationObserver על class בלבד (הכתיבה שלנו היא ל-style, ולכן
     אין לולאה), + resize. אותם סלקטורים כמו ב-glow-daniela-opener (launcherRect). */
  function glowInitStickyAvoid() {
    const sticky = document.querySelector('sticky-atc.glow-sticky');
    if (!sticky || !('MutationObserver' in window)) return;
    const GAP = 8;
    const BOTTOM = 10;
    const place = () => {
      sticky.style.removeProperty('--glow-sticky-left');
      sticky.style.removeProperty('--glow-sticky-right');
      delete sticky.dataset.glowAvoid;
      if (window.innerWidth >= 768 || sticky.classList.contains('sticky-atc--out')) return;
      const launcher = document.querySelector(
        'iframe[name="intercom-launcher-frame"], .intercom-lightweight-app-launcher, .intercom-launcher'
      );
      if (!launcher) return;
      const l = launcher.getBoundingClientRect();
      if (!l.width || !l.height) return;
      /* מיקום המנוחה של הקפסולה, לא ה-rect הנוכחי: בזמן הכניסה היא עוד בתנועה (transform) */
      const restTop = window.innerHeight - BOTTOM - sticky.offsetHeight;
      if (l.bottom <= restTop) return;
      if ((l.left + l.right) / 2 >= window.innerWidth / 2) {
        sticky.style.setProperty('--glow-sticky-right', `${Math.round(window.innerWidth - l.left + GAP)}px`);
      } else {
        sticky.style.setProperty('--glow-sticky-left', `${Math.round(l.right + GAP)}px`);
      }
      sticky.dataset.glowAvoid = 'launcher';
    };
    new MutationObserver(place).observe(sticky, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', place, { passive: true });
    place();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', glowInitStickyAvoid, { once: true });
  } else {
    glowInitStickyAvoid();
  }

  /* glow-facts · חיתוך-תצוגה של השוליים השקופים באינפוגרפיקה (30.9.2026 · r8).
     המאסטרים הם PNG עם ריק שקוף אפוי למעלה ולמטה (4%-21%, שונה בכל מוצר),
     והוא יצר רווח ריק בין "שיתוף" לסקשן ובין התמונה לכותרת במובייל. הקובץ לא
     נוגע (מקור יחיד לנכס): מודדים את שורות הדיו בתמונה שכבר נטענה, ומזינים
     ל-CSS את יחס החלק הנראה (ראה .is-ink-cropped ב-glow-pdp.css).
     ה-canvas קטן (320px, ~2px לשורה בתצוגה) ופועל פעם אחת לתמונה, אחרי load. ⛔ בלי קריאה מחוץ
     ל-origin: אם ה-canvas מזוהם (CDN חיצוני) getImageData זורק → לא חותכים. */
  /* @glow-ink-bounds:start */
  function glowInkBounds(data, w, h, alphaMin = 16, minCols = 2) {
    let top = -1;
    let bottom = -1;
    for (let y = 0; y < h; y++) {
      let n = 0;
      for (let x = 0; x < w; x++) {
        if (data[(y * w + x) * 4 + 3] > alphaMin) n++;
      }
      if (n > minCols) {
        if (top < 0) top = y;
        bottom = y;
      }
    }
    if (top < 0) return null;
    /* שורת ביטחון אחת מכל צד: הדגימה מוקטנת (320px), ושורה חלקית של צל רך
       לא נחתכת */
    top = Math.max(0, top - 1);
    bottom = Math.min(h - 1, bottom + 1);
    return { top: top / h, bottom: (h - 1 - bottom) / h };
  }
  /* @glow-ink-bounds:end */

  function glowCropFactsImage(img) {
    const run = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      try {
        const w = 320;
        const h = Math.max(1, Math.round((w * img.naturalHeight) / img.naturalWidth));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const b = glowInkBounds(ctx.getImageData(0, 0, w, h).data, w, h);
        if (!b) return;
        const cut = b.top + b.bottom;
        if (cut < 0.02 || cut > 0.6) return;
        img.style.setProperty('--glow-facts-ar', `${img.naturalWidth} / ${(img.naturalHeight * (1 - cut)).toFixed(1)}`);
        img.style.setProperty('--glow-facts-pos', `${((b.top / cut) * 100).toFixed(2)}%`);
        img.classList.add('is-ink-cropped');
      } catch (e) {
        /* canvas מזוהם / לא נתמך → התמונה נשארת מלאה */
      }
    };
    if (img.complete && img.naturalWidth) run();
    else img.addEventListener('load', run, { once: true });
  }

  function glowCropFactsImages() {
    for (const img of document.querySelectorAll('.glow-facts__img')) glowCropFactsImage(img);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', glowCropFactsImages, { once: true });
  } else {
    glowCropFactsImages();
  }

  document.addEventListener('click', (event) => {
    const shareLink = event.target.closest('[data-glow-share-channel]');
    if (shareLink) {
      glowPublish('glow_share', {
        handle: shareLink.getAttribute('data-glow-share-handle') || '',
        channel: shareLink.getAttribute('data-glow-share-channel') || '',
      });
      return;
    }
    const relLink = event.target.closest('.glow-related__link[data-glow-handle]');
    if (relLink) {
      glowPublish('glow_select_item', {
        handle: relLink.getAttribute('data-glow-handle') || '',
        list: 'pdp_related',
      });
    }
  });
})();
